"use client"
import { locale } from "@locale";
import { useMediaQuery } from '@react-hooks-library/core';
import cn from "classnames";
import { getCookie, setCookie } from "cookies-next/client";
import { useCallback, useEffect, useState } from 'react';
import Ad from 'src/components/ads/Ad';
import { useAdContext } from "src/lib/types/ads/AdContextType";
import styles from './StickyBottomAd.module.css';

const TARGET_ID = "FW_bottomSticky";
const BRAND = process.env.NEXT_PUBLIC_WEBSITE_BRAND;

//#region [Props]
type StickyBottomAdProps = {
    className?: string;
    isPackshot?: boolean;
};
//#endregion

//#region [Component]
export default function StickyBottomAd({ className, isPackshot = false }: StickyBottomAdProps) {
    if (BRAND === "kinodk") {
        return null;
    }
    return <FilmwebStickyBottomAd className={className} isPackshot={isPackshot} />
}
//#endregion

//#region [Component]
function FilmwebStickyBottomAd({ className, isPackshot = false }: StickyBottomAdProps) {
    const [canHideAd, setCanHideAd] = useState(false);
    const [showAd, setShowAd] = useState(false);
    const isMqMatch = useMediaQuery("(max-width: 549px)");
    const adContext = useAdContext();

    //console.debug("isPackshot", isPackshot)

    // derived during render: an effect for this ran before the one below, which then showed the ad anyway
    const hasAd = !adContext.noMatchedAds?.includes(TARGET_ID) && !!adContext.getAuId(TARGET_ID);

    useEffect(() => {
        const cookieValue = getCookie("fwhba", { path: '/', sameSite: 'strict' })
        if (cookieValue === "true") {
            return;
        }
        //console.debug("Set showAd = ", isMqMatch || isPackshot);
        // eslint-disable-next-line react-hooks/set-state-in-effect -- the cookie and the media query are only known in the browser, so it is read after hydration
        setShowAd(isMqMatch || isPackshot);
        if (isMqMatch || isPackshot) {
            const timerHandle = setTimeout(() => {
                setCanHideAd(true);
            }, 3000);

            return () => {
                clearTimeout(timerHandle);
            }
        }
    }, [isMqMatch, isPackshot]);

    const hideClickHandler = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        if (canHideAd) {
            setCookie("fwhba", "true", { path: '/', maxAge: /*604800*/259_200, sameSite: 'strict' }); // maxAge: 7 days
            //console.debug("Set showAd = false");
            setShowAd(false);
        }
    }, [canHideAd]);

    if (!showAd || !hasAd) {
        return null;
    }

    return <div className={cn(styles.SStickyBottomAd, className, { [styles.packshot]: isPackshot })} aria-label={locale.ads.adLabel}>
        <button aria-label={locale.ads.hideAd} onClick={hideClickHandler} className={cn(styles.SHideButton, { [styles.packshot]: isPackshot })}>
            {locale.ads.adLabel} <CloseSvg className={cn(styles.SCloseSvg, { [styles['can-hide']]: canHideAd })} />
        </button>
        <Ad targetId={TARGET_ID} className={cn(styles.SAd, { [styles.packshot]: isPackshot })} canHide={false} noHeader />
    </div>;
}
//#endregion

//#region [Component] Custom Close Svg
type CloseSvgProps = {
    className?: string;
};

function CloseSvg({ className }: CloseSvgProps) {
    return (<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" className={className}>
        <g transform="translate(-1125.169 -629.169)">
            <path d="M4.1,4.1a14,14,0,1,1,0,19.8A14,14,0,0,1,4.1,4.1Z" transform="translate(1125.169 629.169)" fill="var(--palette-lilla-hovedfarge)" />
            <g transform="translate(1138.904 633.408) rotate(45)">
                <g transform="translate(6.171)" fill="none" stroke="var(--palette-blue-jeans)" strokeWidth="2">
                    <rect width="2.057" height="14.399" rx="1.028" stroke="none" />
                    <rect x="1" y="1" width="0.057" height="12.399" rx="0.028" fill="none" />
                </g>
                <g transform="translate(0 8.228) rotate(-90)" fill="none" stroke="var(--palette-blue-jeans)" strokeWidth="2">
                    <rect width="2.057" height="14.399" rx="1.028" stroke="none" />
                    <rect x="1" y="1" width="0.057" height="12.399" rx="0.028" fill="none" />
                </g>
            </g>
        </g>
    </svg>
    );

}
//#endregion